import {
  Card,
  CardContent,
} from "@/components/ui/Card";
import { Order } from "@/api/SolarCloudApi";
import { PaymentType } from "@/queries/payments/useCreatePayment";
import PaymentFormFactory from "./PaymentFormFactory";
import PaymentMethod from "./PaymentMethod";
import { cn } from "@/util/cn";
import { useEffect, useMemo, useState } from "react";
import { usePaymentGatekeeperStore } from "../ownership/paymentGatekeeper";
import PaymentDetailsProgress from "./PaymentDetailsProgress";

type PaymentFormProps = {
  order: Order;
  className?: string;
  handlePaymentSuccess: (order: Order) => void;
  setIsPaying: (isPaying: boolean) => void;
  isPaying: boolean;
  fullWidth?: boolean;
  allowPayment?: boolean;
  requiresPaymentDetails?: boolean;
};

export default function PaymentForm({
  order,
  className,
  handlePaymentSuccess,
  setIsPaying,
  isPaying,
  showPayPal,
  fullWidth = false,
  allowPayment = true,
  requiresPaymentDetails = false,
}: PaymentFormProps & { showPayPal?: boolean }) {
  const [selectedMethod, setSelectedMethod] = useState<PaymentType | null>(null);
  const runPaymentGatekeeper = usePaymentGatekeeperStore((state) => state.run);
  const gatekeeperHandler = usePaymentGatekeeperStore((state) => state.handler);
  const paymentDetailsValidity = usePaymentGatekeeperStore(
    (state) => state.paymentDetailsValidity
  );
  const paymentDetailsComplete = usePaymentGatekeeperStore(
    (state) => state.paymentDetailsComplete
  );
  const hasPaymentDetailsForm = requiresPaymentDetails || !!gatekeeperHandler;
  const paymentUnlocked =
    allowPayment && (!hasPaymentDetailsForm || paymentDetailsComplete);

  const availableMethods = useMemo(
    () => [
      PaymentType.Mint,
      PaymentType.Apple,
      PaymentType.Google,
      PaymentType.Afterpay,
      PaymentType.Klarna,
      PaymentType.PayTo,
    ].concat(showPayPal ? [PaymentType.PayPal, PaymentType.PayPalPayIn4] : []),
    [showPayPal]
  );

  useEffect(() => {
    if (!paymentUnlocked) {
      setSelectedMethod(null);
      return;
    }

    setSelectedMethod((currentMethod) => {
      if (currentMethod && availableMethods.includes(currentMethod)) {
        return currentMethod;
      }

      return availableMethods.includes(PaymentType.Apple)
        ? PaymentType.Apple
        : availableMethods[0] ?? null;
    });
  }, [availableMethods, paymentUnlocked]);

  return (
    <div className={cn(fullWidth ? "w-full" : "w-full max-w-lg mx-auto", className)}>
      <Card className="bg-transparent border-transparent shadow-none">
        <CardContent className="p-0">
          <div className="space-y-6">
            {hasPaymentDetailsForm && !paymentDetailsComplete && (
              <PaymentDetailsProgress validity={paymentDetailsValidity} />
            )}
            <PaymentMethod
              selectedMethod={selectedMethod}
              onMethodSelect={setSelectedMethod}
              availableMethods={availableMethods}
              disabled={!paymentUnlocked}
              renderSelectedContent={(method) =>
                paymentUnlocked ? (
                  <PaymentFormFactory
                    selectedMethod={method}
                    order={order}
                    onSuccess={handlePaymentSuccess}
                    isSubmitting={isPaying}
                    setIsSubmitting={setIsPaying}
                    beforePayment={runPaymentGatekeeper}
                  />
                ) : null
              }
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
